import React, { useCallback, useEffect, useState } from 'react';
import { Button, List, Empty, Spin, message, Tag, Input } from 'antd';
import { EyeOutlined, PlusOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import api from '../api/client';

interface DocumentItem {
  id: number;
  title: string | null;
  number: string | null;
  date: string | null;
  status: string;
  created_at: string;
  page_count: number;
  legal_entity_ids: string[];
  doc_type: { name: string } | null;
}

const STATUS_LABELS: Record<string, string> = {
  draft: 'Черновик',
  processed: 'Обработан',
  archived: 'Архив',
};

const STATUS_COLORS: Record<string, string> = {
  draft: 'default',
  processed: 'success',
  archived: 'blue',
};

const ENTITY_LABELS: Record<string, string> = {
  autoregium: 'АвтоРегиум',
  fomin: 'Фомин',
};

const DocumentsListPage: React.FC = () => {
  const navigate = useNavigate();
  const [documents, setDocuments] = useState<DocumentItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');

  const load = useCallback(() => {
    setLoading(true);
    api.get('/documents', { params: { search: search || undefined } })
      .then(r => setDocuments(r.data || []))
      .catch(() => message.error('Ошибка загрузки'))
      .finally(() => setLoading(false));
  }, [search]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <h2 style={{ margin: 0 }}>Сканированные документы</h2>
        <Button type="primary" icon={<PlusOutlined />} onClick={() => navigate('/documents/scan')}>
          Сканировать
        </Button>
      </div>

      <Input.Search
        placeholder="Поиск по номеру или названию"
        allowClear
        value={search}
        onChange={e => setSearch(e.target.value)}
        onSearch={load}
        style={{ marginBottom: 16, maxWidth: 400 }}
      />

      <List
        loading={loading}
        dataSource={documents}
        locale={{ emptyText: <Empty description="Нет документов" /> }}
        renderItem={doc => (
          <List.Item
            actions={[
              <Button icon={<EyeOutlined />} size="small" onClick={() => navigate(`/documents/${doc.id}`)}>
                Открыть
              </Button>,
            ]}
          >
            <List.Item.Meta
              title={
                <span>
                  {doc.title || 'Без названия'}
                  <Tag color={STATUS_COLORS[doc.status] || 'default'} style={{ marginLeft: 8 }}>
                    {STATUS_LABELS[doc.status] || doc.status}
                  </Tag>
                </span>
              }
              description={
                <div style={{ fontSize: 13, color: '#888' }}>
                  {doc.doc_type?.name && <span>{doc.doc_type.name} · </span>}
                  {doc.number && <span>№{doc.number} · </span>}
                  {doc.date && <span>{doc.date} · </span>}
                  <span>стр.: {doc.page_count} · </span>
                  <span>{new Date(doc.created_at).toLocaleDateString('ru-RU')}</span>
                  {doc.legal_entity_ids?.length > 0 && (
                    <span style={{ marginLeft: 8 }}>
                      {doc.legal_entity_ids.map(e => (
                        <Tag key={e}>{ENTITY_LABELS[e] || e}</Tag>
                      ))}
                    </span>
                  )}
                </div>
              }
            />
          </List.Item>
        )}
      />
    </div>
  );
};

export default DocumentsListPage;
